const state = {
  list: [],
  campaign: {},
  targetOptions: [],
  targetUrls: [],
  settings: {}
};

const getters = {
  list: (state) => state.list || [],
  campaign: (state) => state.campaign || {},
  targetOptions: (state) => state.targetOptions || [],
  targetUrls: (state) => state.targetUrls || [],
  settings: (state) => state.settings || {}
};

const mutations = {
  SET_LIST(state, list) {
    state.list = list;
  },
  SET_CAMPAIGN(state, item) {
    state.campaign = item;
    // The list is fetched once and reused by the list/calendar views, so an edit made on the detail page
    // (approving an item, moving its send date, sending) would otherwise leave them showing stale statuses
    // until a manual refresh. Get and GetAll return the same shape, so the fresh campaign can replace its
    // list entry outright.
    if (item && item.id) {
      state.list = (state.list || []).map((c) => (c.id === item.id ? item : c));
    }
  },
  // The list is fetched once and shared by the list/calendar views, so a delete has to drop the row here too
  // or the campaign lingers on both until a manual refresh. The detail slot is cleared as well when it's the
  // campaign that just went away, so nothing renders a deleted campaign's copy.
  REMOVE_CAMPAIGN(state, id) {
    state.list = (state.list || []).filter((c) => c.id !== id);
    if (state.campaign && state.campaign.id === id) state.campaign = {};
  },
  SET_TARGET_OPTIONS(state, options) {
    state.targetOptions = options;
  },
  SET_TARGET_URLS(state, urls) {
    state.targetUrls = urls;
  },
  SET_SETTINGS(state, settings) {
    state.settings = settings;
  }
};

const actions = {
  getList({ commit }) {
    return new Promise((resolve, reject) => {
      this._vm.$http
        .get('campaigns/getAll')
        .then((response) => {
          commit('SET_LIST', response.data);
          resolve(response.data);
        })
        .catch((error) => reject(error));
    });
  },
  get({ commit }, id) {
    return new Promise((resolve, reject) => {
      this._vm.$http
        .get(`campaigns/get?id=${id || 0}`)
        .then((response) => {
          commit('SET_CAMPAIGN', response.data);
          resolve(response.data);
        })
        .catch((error) => reject(error));
    });
  },
  getTargetOptions({ commit }) {
    return new Promise((resolve, reject) => {
      this._vm.$http
        .get('campaigns/getTargetOptions')
        .then((response) => {
          commit('SET_TARGET_OPTIONS', response.data);
          resolve(response.data);
        })
        .catch((error) => reject(error));
    });
  },
  // Suggestions for the Target URL field — content pages plus every catalog page.
  getTargetUrls({ commit }) {
    return new Promise((resolve, reject) => {
      this._vm.$http
        .get('campaigns/getTargetUrls')
        .then((response) => {
          commit('SET_TARGET_URLS', response.data);
          resolve(response.data);
        })
        .catch((error) => reject(error));
    });
  },
  getSettings({ commit }) {
    return new Promise((resolve, reject) => {
      this._vm.$http
        .get('campaigns/getSettings')
        .then((response) => {
          commit('SET_SETTINGS', response.data);
          resolve(response.data);
        })
        .catch((error) => reject(error));
    });
  },
  saveSettings({ commit }, settings) {
    return new Promise((resolve, reject) => {
      this._vm.$http
        .post('campaigns/saveSettings', settings)
        .then((response) => {
          commit('SET_SETTINGS', response.data);
          resolve(response.data);
        })
        .catch((error) => reject(error));
    });
  },
  save({ commit }, campaign) {
    return new Promise((resolve, reject) => {
      this._vm.$http
        .post('campaigns/save', campaign)
        .then((response) => {
          commit('SET_CAMPAIGN', response.data);
          resolve(response.data);
        })
        .catch((error) => reject(error));
    });
  },
  generateEmail({ commit }, { id, comments, imageComments, scope, usePriorImage, useTemplateArt }) {
    return new Promise((resolve, reject) => {
      this._vm.$http
        .post('campaigns/generateEmail', { id, comments, imageComments, scope, usePriorImage, useTemplateArt })
        .then((response) => {
          commit('SET_CAMPAIGN', response.data);
          resolve(response.data);
        })
        .catch((error) => reject(error));
    });
  },
  // Writes several scored subject/preheader pairs to choose between. Saves nothing and returns no campaign, so
  // there is deliberately no SET_CAMPAIGN here — applyEmailSubject is what commits the one the editor picks.
  generateSubjectCandidates(_, { id, comments, angle }) {
    return this._vm.$http
      .post('campaigns/generateSubjectCandidates', { id, comments, angle })
      .then((r) => r.data);
  },
  // The angles a subject line can take. Served from the SubjectAngle enum rather than listed here, so the picker
  // can't drift from the prompt vocabulary or from the values the Subject lines report groups on.
  getSubjectAngles() {
    return this._vm.$http.get('campaigns/getSubjectAngles').then((r) => r.data);
  },
  // Commits one chosen subject line + preheader. The body HTML and hero image are left exactly as they are.
  applyEmailSubject({ commit }, { itemId, subject, preheader, angle }) {
    return new Promise((resolve, reject) => {
      this._vm.$http
        .post('campaigns/applyEmailSubject', { itemId, subject, preheader, angle })
        .then((response) => {
          commit('SET_CAMPAIGN', response.data);
          resolve(response.data);
        })
        .catch((error) => reject(error));
    });
  },
  // Cross-send roll-up of which kinds of subject line have performed. Read-only reporting, no campaign to commit.
  getSubjectPerformance(_, days) {
    return this._vm.$http
      .get(`campaigns/getSubjectPerformance?days=${days || 0}`)
      .then((r) => r.data);
  },
  generateSocial({ commit }, { id, comments, imageComments, scope, usePriorImage, useTemplateArt }) {
    return new Promise((resolve, reject) => {
      this._vm.$http
        .post('campaigns/generateSocial', { id, comments, imageComments, scope, usePriorImage, useTemplateArt })
        .then((response) => {
          commit('SET_CAMPAIGN', response.data);
          resolve(response.data);
        })
        .catch((error) => reject(error));
    });
  },
  generateSocialItem({ commit }, { id, platform, comments, imageComments, scope, usePriorImage, useTemplateArt }) {
    return new Promise((resolve, reject) => {
      this._vm.$http
        .post('campaigns/generateSocialItem', { id, platform, comments, imageComments, scope, usePriorImage, useTemplateArt })
        .then((response) => {
          commit('SET_CAMPAIGN', response.data);
          resolve(response.data);
        })
        .catch((error) => reject(error));
    });
  },
  // Fire-and-forget: the server queues a Hangfire job that runs many AI generations.
  fill(_, { type, weeks, autoSchedule }) {
    return this._vm.$http.post(
      `campaigns/fill?type=${type}&weeks=${weeks || 4}&autoSchedule=${autoSchedule ? 'true' : 'false'}`
    );
  },
  suggest(_, { brief, type }) {
    // Fire-and-forget: the server queues a background job, so there's no campaign to commit here.
    return this._vm.$http.post('campaigns/suggest', { brief, type });
  },
  // Validates every href in the email against the live site (content/catalog pages), for the links-valid indicator.
  checkLinks(_, itemId) {
    return this._vm.$http.get(`campaigns/checkLinks?itemId=${itemId}`).then((r) => r.data);
  },
  // Hand-edited email content saved over the generated draft; the server normalizes the HTML and returns the campaign.
  saveEmailContent({ commit }, { itemId, subject, preheader, bodyHtml }) {
    return new Promise((resolve, reject) => {
      this._vm.$http
        .post('campaigns/saveEmailContent', { itemId, subject, preheader, bodyHtml })
        .then((response) => {
          commit('SET_CAMPAIGN', response.data);
          resolve(response.data);
        })
        .catch((error) => reject(error));
    });
  },
  approveItem(_, itemId) {
    return this._vm.$http.post(`campaigns/approveItem?id=${itemId}`);
  },
  // Re-queues a failed item: clears the error and re-approves it for the next dispatcher tick.
  retryItem(_, itemId) {
    return this._vm.$http.post(`campaigns/retryItem?id=${itemId}`);
  },
  // Closes out a failed item that actually published, against the external id read off the network.
  // The opposite call to retryItem — that one re-sends, this one accepts the send already happened.
  markItemSent({ commit }, { itemId, externalId, postUrl }) {
    return new Promise((resolve, reject) => {
      this._vm.$http
        .post('campaigns/markItemSent', { itemId, externalId, postUrl })
        .then((response) => {
          commit('SET_CAMPAIGN', response.data);
          resolve(response.data);
        })
        .catch((error) => reject(error));
    });
  },
  // Dates an undated campaign with the same auto-scheduler audience-plan and filled campaigns get; returns the
  // campaign with its new send date on it. `reset` re-picks a date it already has.
  scheduleCampaign({ commit }, { id, reset }) {
    return new Promise((resolve, reject) => {
      this._vm.$http
        .post(`campaigns/scheduleCampaign?id=${id}&reset=${!!reset}`)
        .then((response) => {
          commit('SET_CAMPAIGN', response.data);
          resolve(response.data);
        })
        .catch((error) => reject(error));
    });
  },
  // Pulls a scheduled campaign back to draft: the server un-approves every unsent item so nothing auto-sends.
  // The planned date is kept — that's what scheduleCampaign/setSendDate are for.
  unschedule({ commit }, id) {
    return new Promise((resolve, reject) => {
      this._vm.$http
        .post(`campaigns/unschedule?id=${id}`)
        .then((response) => {
          commit('SET_CAMPAIGN', response.data);
          resolve(response.data);
        })
        .catch((error) => reject(error));
    });
  },
  setSendDate(_, { itemId, when }) {
    return this._vm.$http.post('campaigns/setSendDate', { itemId, when });
  },
  sendTest(_, id) {
    // Sends the campaign email only to the current admin; returns { email } and leaves the campaign untouched.
    return this._vm.$http.post(`campaigns/sendTest?id=${id}`).then((r) => r.data);
  },
  getAudience(_, id) {
    // Returns { workAreas, estimatedRecipients } for the campaign's email audience.
    return this._vm.$http.get(`campaigns/getAudience?id=${id}`).then((r) => r.data);
  },
  getAudienceCount(_, id) {
    // Authoritative Customer.io count (paged lookup) — { count, isPartial, error }. Behind a button; slower.
    return this._vm.$http.get(`campaigns/getAudienceCount?id=${id}`).then((r) => r.data);
  },
  getAiSpend(_, id) {
    // This campaign's AI cost broken out by step (suggest / email / subject / social / ads / imagery).
    // The total already rides on the campaign itself; this is the detail behind it, fetched on expand.
    return this._vm.$http.get(`campaigns/getAiSpend?id=${id}`).then((r) => r.data);
  },
  getEmailStats(_, id) {
    // How the send actually performed, aggregated from the Customer.io reporting events we store. Customer.io
    // can't report this per campaign (one broadcast serves them all), so the events are matched on the trigger
    // id it returned at send. Behind a button, like getAudienceCount.
    return this._vm.$http.get(`campaigns/getEmailStats?id=${id}`).then((r) => r.data);
  },
  remove({ commit }, id) {
    return this._vm.$http.post(`campaigns/delete?id=${id}`).then((response) => {
      commit('REMOVE_CAMPAIGN', id);
      return response;
    });
  },
  // Where this campaign's pin files, plus the boards available to move it to. Read-only: the server
  // deliberately does NOT create the board just because someone opened the page.
  getPinterestTarget(_, id) {
    return this._vm.$http.get(`campaigns/getPinterestTarget?id=${id}`).then((r) => r.data);
  },
  setPinterestBoard(_, { itemId, boardId }) {
    return this._vm.$http.post('campaigns/setPinterestBoard', { itemId, boardId });
  },
  pinterestStatus() {
    return this._vm.$http.get('campaigns/pinterestStatus').then((r) => r.data);
  },
  pinterestAuthUrl() {
    return this._vm.$http.get('campaigns/pinterestAuthUrl').then((r) => r.data);
  },
  pinterestDisconnect() {
    return this._vm.$http.post('campaigns/pinterestDisconnect');
  }
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};
